import { ValidatorFn, AbstractControl, Validators, ValidationErrors, AsyncValidatorFn, FormControl } from "@angular/forms";
import Utils from "../utils";
import { OrderDataInputMaxLength, DeliveryAddressQueryMaxLength, InputValidationMinLength, InputValidationMaxLength } from "../contentTypes";
import { from } from "rxjs/internal/observable/from";
import { catchError, map } from "rxjs/operators";
import { of } from "rxjs/Observable/of";
import { Observable } from "rxjs";
import { CustomerInputFieldViewModel } from "../models";
import { IErrorMessage } from "./validatorMessage.interface";

//#region VALIDATORS CONFIG CLASS

export class ValidatorsConfig {

  //region PATTERNS

  /** Alphanumeric with German umlauts and ß, plus spaces (e.g. name IH-GroupName) */
  static readonly PATTERN_ALPHANUMERIC = /^(?:[a-zA-Z0-9\u00c4\u00e4\u00d6\u00f6\u00dc\u00fc\u00df\s]+)?$/;

  /** Address/ZIP (alphanumeric + hyphen + space + German umlauts and ß) */
  static readonly PATTERN_ALPHANUMERIC_BASIC = /^(?:[a-zA-Z0-9\u0020\u002d\u00c4\u00e4\u00d6\u00f6\u00dc\u00fc\u00df\s]+)?$/;

  /** Alphanumeric with German umlauts and ß, spaces, and punctuation (:',) e.g. Does's group*/
  static readonly PATTERN_ALPHANUMERIC_EXTENDED = /^(?:[a-zA-Z0-9\u00c4\u00e4\u00d6\u00f6\u00dc\u00fc\u00df\s':]+)?$/;

  /** Letters only, including German umlauts and ß, spaces, hyphen, and forward slash */
  static readonly PATTERN_LETTERS_ONLY = /^(?:[a-zA-Z\u00C0-\u00FF\u0100-\u017F\u00F1\u00E7\u00F6\u00FC\u00F1\u00E9\u00E0\u00E8\u00E2\u00F6\u00F4\u00F9\u00E7\u00E9\u00C9\u00F3\u00FA\u00ED\u00F1\u00D1\u00A0\u0020\u002F\u005C\-]+)?$/;

  /** Containing letters (including extended Latin letters with accents),
   * whitespace, periods, hyphens, parentheses, and apostrophes.
   */
  static readonly PATTERN_LETTERS_EXTENDED = /^(?:[a-zA-ZÀ-ÿ\s.\-()'’]+)?$/;

  /** Numbers and spaces only */
  static readonly PATTERN_NUMBERS_ONLY = /^(?:[0-9\s]+)?$/;

  /** Numbers only */
  static readonly PATTERN_DECIMAL_NUMBER = /^\d+(?:,\d{1,2})?$/;

  /** House number (at least one digit, alphanumeric with German umlauts and ß, and special characters) */
  static readonly PATTERN_HOUSE_NUMBER = /^(?=.*\d)[a-zA-Z0-9\u00c4\u00e4\u00d6\u00f6\u00dc\u00fc\u00df\s\-+#\/.]+$/;

  /** Combined address: min 3 letters + min 1 digit*/
  static readonly PATTERN_ADDRESS_COMBINED_WITH_NUMBER = /^(?=(?:.*[a-zA-Z\u00C0-\u017F\u00DF]){3,})(?=.*\d)[a-zA-Z\u00C0-\u017F\u00DF0-9 .'\u2019\-\/]*$/;

  /** Voucher code: exactly 18 digits */
  static readonly PATTERN_VOUCHER = /^\d{18}$/;

  /** Accepts phone numbers with flexible grouping and common separators, including spaces, parentheses, dots, slashes, hyphens, and Unicode dashes. Separators are only allowed between digit groups and parentheses must be balanced, so cosmetic input like "()()123456" or "---123456---" is rejected. Numbers must contain 6–15 digits, may start with +, and must not contain letters or invalid prefixes like ++. */
  static readonly PATTERN_PHONE = /^(?=(?:\D*\d){6,15}\D*$)\s*\+?\s*(?:\(\d+\)|\d+)(?:[\s.\/\\‐‑‒–—−-]*(?:\(\d+\)|\d+))*\s*$/;

  static readonly PATTERN_EMAIL = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  /** Date pattern DD.MM.YYYY */
  static readonly PATTERN_DATE_DD_MM_YYYY = /(\d{1,2})\.(\d{1,2})\.(\d{4})/;

  //endregion PATTERNS

  //region VALIDATORS

  /** Validator for ZIP */
  static forZipCode(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(OrderDataInputMaxLength.zip_code),
      Validators.pattern(this.PATTERN_ALPHANUMERIC_BASIC)
    ];
  }

  /** Validator for city or region name */
  static forCity(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(OrderDataInputMaxLength.city),
      Validators.pattern(this.PATTERN_LETTERS_EXTENDED)
    ];
  }

  /** Validator for country name */
  static forCountry(): ValidatorFn[] {
    return [
      Validators.maxLength(OrderDataInputMaxLength.country),
      Validators.pattern(this.PATTERN_LETTERS_ONLY)
    ];
  }

  /** Validator for street name */
  static forStreet(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(DeliveryAddressQueryMaxLength.street),
      Validators.minLength(InputValidationMinLength.street)
    ];
  }

  /** Validator for house number */
  static forHouseNumber(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(DeliveryAddressQueryMaxLength.houseNumber),
      Validators.pattern(this.PATTERN_HOUSE_NUMBER)
    ];
  }

  /** Validator for combined address (street + house number in one field) */
  static forAddressCombined(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(OrderDataInputMaxLength.address), // 40
      Validators.pattern(this.PATTERN_ADDRESS_COMBINED_WITH_NUMBER) // Must contain at least one digit
    ];
  }

  /** Validator for address addition (e.g., apartment or building info) */
  static forAddressAddition(): ValidatorFn[] {
    return [
      Validators.maxLength(OrderDataInputMaxLength.addition)
    ];
  }

  /** Validator for c/o  */
  static forCareOf(): ValidatorFn[] {
    return [
      Validators.maxLength(OrderDataInputMaxLength.co)
    ];
  }

  /** Validates phone numbers when a country code is selected (e.g., for SMS notifications). */
  static forPhoneNumber(): ValidatorFn[] {
    return [
      Validators.minLength(InputValidationMinLength.phone),
      Validators.maxLength(OrderDataInputMaxLength.phone_number),
      Validators.pattern(this.PATTERN_NUMBERS_ONLY)
    ];
  }

  /** Validates telephone number (country code is not selected) */
  static forTelephone(): ValidatorFn[] {
    return [
      Validators.maxLength(InputValidationMaxLength.telephone1),
      Validators.pattern(this.PATTERN_PHONE)
    ];
  }

  static forEmail(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(InputValidationMaxLength.email),
      Validators.pattern(this.PATTERN_EMAIL)
    ];
  }

  /**Validates prefix */
  static forPrefix(): ValidatorFn[] {
    return [Validators.maxLength(InputValidationMaxLength.prefix),];
  }

  /** Validates the lastname */
  static forLastName(): ValidatorFn[] {
    return [
      Validators.maxLength(InputValidationMaxLength.name),
      Validators.pattern(this.PATTERN_LETTERS_ONLY)
    ];
  }

  /** Validates the username */
  static forFirstName(): ValidatorFn[] {
    return [
      Validators.minLength(InputValidationMinLength.fname),
      Validators.maxLength(InputValidationMaxLength.fname),
      Validators.pattern(this.PATTERN_LETTERS_ONLY)
    ];
  }

  /** Validates the member name or groupname (e.g. IH order and remote order nickname) */
  static forGroupMemberName(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.minLength(InputValidationMinLength.name),
      Validators.maxLength(InputValidationMaxLength.name),
      Validators.pattern(this.PATTERN_LETTERS_ONLY),
      this.noWhitespaceOnly()
    ];
  }

  /** Validates the remote order group name */
  static forRemoteOrderGroupName(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.minLength(InputValidationMinLength.name),
      Validators.maxLength(InputValidationMaxLength.name),
      Validators.pattern(this.PATTERN_ALPHANUMERIC_EXTENDED),
      this.noWhitespaceOnly()
    ];
  }

  /** Validator for voucher code (18 digits) */
  static forVoucherCode(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.pattern(this.PATTERN_VOUCHER)
    ];
  }

  /** Validates the custom web voucher code */
  static forCustomWebVoucher(): ValidatorFn[] {
    return [
      Validators.pattern(this.PATTERN_NUMBERS_ONLY)
    ];
  }

  /** Validates that the value is not only whitespace */
  static noWhitespaceOnly(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;
      if (value == null || value === '') { return null; }
      return value.trim() === '' ? { noWhitespaceOnly: true } : null;
    };
  }

  static getValidatorsForDynamicFields(CustomerInputFieldViewModel: CustomerInputFieldViewModel): ValidatorFn[] {
    let validators: ValidatorFn[] = [];

    // If field is not required, only add maxLength validator
    if (!CustomerInputFieldViewModel.isRequired) {
      return [Validators.maxLength(200)];
    }

    // For required fields, add all relevant validators
    switch (CustomerInputFieldViewModel.fieldName) {
      case 'prefix': validators = this.forPrefix(); break;
      case 'name': validators = this.forLastName(); break;
      case 'fname': validators = this.forFirstName(); break;
      case 'address': validators = this.forAddressCombined(); break;
      case 'zip_code': validators = this.forZipCode(); break;
      case 'city': validators = this.forCity(); break;
      case 'country': validators = this.forCountry(); break;
      case 'addition': validators = this.forAddressAddition(); break;
      case 'email': validators = this.forEmail(); break;
      case 'co': validators = this.forCareOf(); break;
      case 'telephone1': case 'telephone2': case 'telephone_mobile': validators = this.forTelephone(); break;
      case 'bday_ymd': validators = [birthDateValidator()]; break;
      default: validators = [];
    }

    // Add required validator only if field is required and not already present
    if (CustomerInputFieldViewModel.isRequired && !validators.some(v => v.name === 'required')) {
      validators.push(Validators.required);
    }

    return validators;
  }

  /** Get error message key from FormControl */
  static getErrorMessageKey(control: FormControl, errorMessages: IErrorMessage[]): string {
    if (!control || !control.errors) return '';
    for (const errorMsg of errorMessages) {
      if (control.errors[errorMsg.type]) {
        return errorMsg.message;
      }
    }

    return '';
  }

  /**
   * Get active error message for a form control based on field type
   * @param control - FormControl to check for errors
   * @param errorMessagesMap - Map of field names to error message arrays
   * @param fieldName - Field name to look up error messages
   * @returns Error message key or null if no error
   */
  static getActiveErrorMessage(control: FormControl, errorMessagesMap: { [key: string]: IErrorMessage[] }, fieldName: string): string | null {
    const errors = control.errors;
    if (!errors) return null;

    const errorMessages = errorMessagesMap[fieldName];
    if (!errorMessages) return null;

    for (const error of errorMessages) {
      if (errors[error.type]) return error.message;
    }
    return null;
  }
}

//#endregion VALIDATORS


//#region CUSTOM VALIDATORS

export function ratingValueValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return (!Utils.IsNull(control.value) && control.value > 0) ? null : { "rateValue": { value: 0 } };
  };
}

export function passwordValueValidator(): ValidatorFn {
  const regexPassword = new RegExp(/(?=.*[0-9])(?=.*[a-z,A-Z]).{8,}/);

  return (control: AbstractControl): { [key: string]: any } | null => {
    return (Utils.IsNull(control.value) || (!Utils.IsNull(control.value) && regexPassword.test(control.value))) ? null : { "passwordValidation": { value: true } };
  };
}

export function emailValueValidator(): ValidatorFn {
  const regexEmail = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

  return (control: AbstractControl): { [key: string]: any } | null => {
    return (Utils.IsNull(control.value) || (!Utils.IsNull(control.value) && regexEmail.test(control.value))) ? null : { "emailValidation": { value: true } };
  };
}

export function emailConfirmationValidator(newPassword: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return (!Utils.IsNull(control.value) && control.value === newPassword) ? null : { "emailConfirmation": { value: 0 } };
  };
}

export function customUserNameValidator(): ValidatorFn {
  const regexPassword = new RegExp(/^(?=.*[a-zA-Z]).+$/);
  return (control: AbstractControl): { [key: string]: any } | null => {
    return (Utils.IsNull(control.value) || (!Utils.IsNull(control.value) && regexPassword.test(control.value))) ? null : { "validation": { value: true } };
  };
}

export function birthDateValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (Utils.IsNull(control.value) || control.value === '') {
      return null; // Let Validators.required handle empty values
    }

    const dateValue = +control.value;

    // Check if it's a valid 8-digit number
    if (isNaN(dateValue) || dateValue < 10000101 || dateValue > 99991231) {
      return { "dateInvalid": { value: true } };
    }

    // Extract year, month, day from YYYYMMDD format
    const year = Math.floor(dateValue / 10000);
    const month = Math.floor((dateValue % 10000) / 100);
    const day = dateValue % 100;

    // Validate using Utils.isValidateDate
    const isValid = Utils.isValidateDate(year, month - 1, day);

    return isValid ? null : { "dateInvalid": { value: true } };
  };
}

/** Async validator for dynamic minimum length validation */
export function dynamicMinLengthValidator(getRequiredLength: () => Promise<number>): AsyncValidatorFn {
  return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    return getRequiredLength().then((val) => {
      return control.value.length < val ? { minLengthError: true } : null;
    });
  };
}

/** Async validator for dynamic maximum length validation */
export function dynamicAsyncMaxLengthValidator(getRequiredLength: () => Promise<number>): AsyncValidatorFn {
  return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    return getRequiredLength().then((val) => {
      return control.value.length > val ? { maxLengthError: true } : null;
    });
  };
}

/** Async validator to check username against blacklist */
export function forbiddenNameValidator(webShopService: { getWebshopSettingsDataModel(): Promise<{ name_blacklist?: string[] }> }): AsyncValidatorFn {
  return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    // Skip async validation for empty values (let required validator handle it)
    if (!control.value || control.value.trim() === '') {
      return Promise.resolve(null);
    }

    return checkNameAgainstBlacklist(control.value, webShopService)
      .then(result => {
        return !result ? { forbiddenName: true } : null;
      });
  };
}

/** Helper function to check name against webshop blacklist */
async function checkNameAgainstBlacklist(name: string, webShopService: { getWebshopSettingsDataModel(): Promise<{ name_blacklist?: string[] }> }): Promise<boolean> {
  const check_name = name ? name.toUpperCase().trim() : "";

  // Empty field is valid for this async validator
  if (check_name === "") {
    return true;
  }

  const webshopSetting = await webShopService.getWebshopSettingsDataModel();

  if (webshopSetting.name_blacklist) {
    const blacklisted_name = webshopSetting.name_blacklist.find((blacklist_name: string) =>
      check_name.includes(blacklist_name.toUpperCase().trim())
    );
    if (blacklisted_name) {
      return false;
    }
  }

  return true;
}

/** Async validator for webvoucher code validation */
export function webVoucherValidator(checkVoucher: (cardnr: string) => Promise<{ success: boolean }>): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    return from(checkVoucher(control.value)).pipe(
      map((model) =>
        model && model.success ? null : { voucherInvalid: true }
      ),
      catchError(() => of({ voucherInvalid: true, getWebVoucherError: true }))
    );
  };
}